import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PricebookSchedule } from "../../../../types/price-book.type";
import {
  BookDiscountChangeLine,
  ChangeCountChips,
} from "../Details/ChangeSummary";
import PriceChangeList from "../Details/PriceChangeList";
import { buildDraftChangeRows } from "../Details/priceChangeRows";
import {
  SCHEDULE_CONFLICT_TEXT,
  SCHEDULE_STATUS_COLOR,
  SCHEDULE_STATUS_LABEL,
  buildScheduleAuditLine,
  formatScheduleEffectiveText,
  isSchedulePending,
} from "./scheduleModel";

const NO_LINE_CHANGES = "ราคาสินค้าไม่ต่างจากต้นฉบับ";

interface ScheduleCardProps {
  schedule: PricebookSchedule;
  /** May cancel a schedule still waiting for its day (a catalogue writer). */
  canCancel: boolean;
  onCancel: (schedule: PricebookSchedule) => void;
}

/**
 * One scheduled draft apply: the day it takes effect, where it stands, who set it, and what it
 * changes on the book as the comparison stood when it was set.
 */
export default function ScheduleCard({ schedule, canCancel, onCancel }: ScheduleCardProps) {
  const changeRows = useMemo(
    () => buildDraftChangeRows(schedule.changes.items),
    [schedule.changes.items],
  );
  const showsCancel = canCancel && isSchedulePending(schedule);
  const conflictText = schedule.conflict_reason
    ? SCHEDULE_CONFLICT_TEXT[schedule.conflict_reason]
    : "";

  const handleCancel = () => onCancel(schedule);

  return (
    <Paper variant="outlined">
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography fontWeight={700}>
              {formatScheduleEffectiveText(schedule.effective_date)}
            </Typography>
            <Chip
              size="small"
              color={SCHEDULE_STATUS_COLOR[schedule.status]}
              label={SCHEDULE_STATUS_LABEL[schedule.status]}
            />
            <Typography variant="body2" color="text.secondary">
              จากร่าง {schedule.draft_code}
            </Typography>
          </Stack>
          {showsCancel && (
            <Button size="small" variant="outlined" color="error" onClick={handleCancel}>
              ยกเลิกการตั้งเวลา
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {buildScheduleAuditLine(schedule)}
        </Typography>
        {conflictText && <Alert severity="error">{conflictText}</Alert>}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <ChangeCountChips summary={schedule.changes.summary} />
        </Stack>
        <BookDiscountChangeLine change={schedule.changes.discount_percent} />
        <PriceChangeList rows={changeRows} emptyText={NO_LINE_CHANGES} />
      </Stack>
    </Paper>
  );
}
